import { FC, useState } from "react"
import { Card, CardContent, CardHeader } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Search, Shield, Trash2 } from "lucide-react"
import { AdminDTO } from "@/gen/api"
import useDeleteAdmin from "@/hooks/api/use-delete-admin"
import { queryClient } from "@/lib/queryClient"
import { DeleteAdminModal } from "./DeleteAdminModal"
import { CreatedAtDisplay, LastLoginDisplay } from "./ui/date-display"
import { useAuth } from "@/hooks/useAuth"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"
import { cn } from "@/lib/utils"
import { Input } from "./ui/input"

interface AdminListProps {
  admins: AdminDTO[]
  isLoading: boolean
  error: Error | null
  onEdit?: (admin: AdminDTO) => void
  onDelete?: (admin: AdminDTO) => Promise<void> | void
  className?: string
}

const roleLabel = (role?: string) => {
  if (role === "super_admin") return "N3 Admin"
  if (role === "council_admin") return "Council Admin"
  return "Admin"
}

const AdminList: FC<AdminListProps> = ({
  admins,
  isLoading,
  error,
  onEdit,
  onDelete,
  className,
}) => {
  const { user: currentUser } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<AdminDTO | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const { mutate: deleteAdmin, isPending: isDeleting } = useDeleteAdmin({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] })
      setIsModalOpen(false)
      setSelectedAdmin(null)
    },
  })

  const handleDeleteClick = (admin: AdminDTO) => {
    setSelectedAdmin(admin)
    setIsModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (selectedAdmin?.id != null) {
      if (onDelete) {
        Promise.resolve(onDelete(selectedAdmin)).then(() => {
          setIsModalOpen(false)
          setSelectedAdmin(null)
        })
        return
      }
      deleteAdmin(String(selectedAdmin.id))
    }
  }

  const normalizedSearch = searchQuery.trim().toLowerCase()
  const filteredAdmins = normalizedSearch
    ? admins.filter((admin) =>
        [
          admin.name,
          admin.firstName,
          admin.lastName,
          admin.email,
          admin.phoneNumber,
          admin.role,
          admin.councilName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch)),
      )
    : admins

  return (
    <Card className={cn("flex min-h-0 flex-col overflow-hidden", className)}>
      <CardHeader className="shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search admins"
            className="pl-9"
          />
        </div>
      </CardHeader>
      <CardContent className="min-h-0 flex-1 overflow-hidden">
        <div className="h-full space-y-4 overflow-auto pr-1">
          {isLoading && <div>Loading admins...</div>}
          {error && <div>Error loading admins: {error.message}</div>}
          {!isLoading &&
            !error &&
            filteredAdmins.map((admin) => (
              <div key={admin.id?.toString() ?? ""} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="font-medium">{admin.name}</h3>
                    <p className="text-sm text-muted-foreground">{admin.email}</p>
                    {admin.councils && admin.councils.length > 1 && (
                      <p className="text-sm text-muted-foreground">
                        Councils:{" "}
                        {admin.councils
                          .map((council) => council.name)
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {admin.councilName && (
                      <p className="text-sm text-muted-foreground">Council: {admin.councilName}</p>
                    )}
                  </div>
                  <div className="text-right space-y-2">
                    <Badge variant={admin.role === "super_admin" ? "default" : "secondary"}>
                      {roleLabel(admin.role)}
                    </Badge>
                    {admin.enforceTwoFactor && (
                      <Badge variant="outline" className="gap-1">
                        <Shield className="size-3" />
                        2FA {admin.twoFactorEnabled ? "enabled" : "required"}
                      </Badge>
                    )}
                  </div>
                </div>

                {(admin.phoneNumber || (admin.permissions && admin.permissions.length > 0)) && (
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {admin.phoneNumber && <span>Phone: {admin.phoneNumber}</span>}
                    {admin.permissions && admin.permissions.length > 0 && (
                      <span>{admin.permissions.length} permissions</span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Created: <CreatedAtDisplay date={admin.createdAt ?? null} />
                    </p>
                    {admin.lastLogin && (
                      <p className="text-xs text-muted-foreground">
                        Last Login: <LastLoginDisplay date={admin.lastLogin ?? null} />
                      </p>
                    )}
                  </div>
                  <div className="space-x-2">
                    {onEdit && (
                      <Button size="sm" variant="outline" onClick={() => onEdit(admin)}>
                        Details
                      </Button>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteClick(admin)}
                            disabled={isDeleting || admin.id === currentUser?.id}
                          >
                            <Trash2 className="size-4 mr-1" />
                            Delete
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {admin.id === currentUser?.id && (
                        <TooltipContent>
                          <p>This is you</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}

          {!isLoading && !error && admins.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No admins found.</p>
            </div>
          )}

          {!isLoading && !error && admins.length > 0 && filteredAdmins.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No admins match your search.</p>
            </div>
          )}
        </div>
      </CardContent>

      <DeleteAdminModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        adminName={selectedAdmin?.name || ""}
        onConfirm={handleConfirmDelete}
      />
    </Card>
  )
}

export default AdminList
